<template>
  <div class="user-info-tab pa-4">
    <BaseInfoSection
      :user-id="user.id"
      :user-name="user.name"
      :user-gender="daytimeAdviceSheet.userGender"
      :last-due-date="daytimeAdviceSheet.lastDueDate"
      :coupon="displayCoupon"
      :coupon-id="displayCouponId"
    />

    <NoteSection
      title="運営からの特記事項"
      :content="user.operatorNotice"
      :editable="true"
      :can-edit="canEditOperatorNote"
      :is-editing="isEditingOperatorNote"
      :draft="operatorNoticeDraft"
      :is-saving="operatorNoteSaving"
      @toggle-edit="toggleOperatorNoteEdit"
      @update:draft="operatorNoticeDraft = $event"
      @cancel-edit="cancelOperatorNoteEdit"
    />

    <NoteSection
      title="ユーザーの特記事項"
      :content="user.notice"
      :editable="true"
      :can-edit="canEditNotes"
      :is-editing="isEditingUserNote"
      :draft="userNoticeDraft"
      :is-saving="userNoteSaving"
      @toggle-edit="toggleUserNoteEdit"
      @update:draft="userNoticeDraft = $event"
      @cancel-edit="cancelUserNoteEdit"
    />

    <ChildInfoSection
      :children="children"
      :loading="childrenLoading"
      :is-editing="isEditingChildren"
      :drafts="childNoticeDrafts"
      :can-edit="canEditNotes"
      :is-saving="childNoteSaving"
      @start-edit="startChildNoteEdit"
      @save-edit="saveChildNoteEdit"
      @update:drafts="childNoticeDrafts = $event"
      @cancel-edit="cancelChildNoteEdit"
    />

    <ConsultationHistorySection
      :histories="consultationHistories"
      @open="openConsultation"
    />

    <EpdsHistorySection
      :histories="epdsHistories"
      @open="openEpds"
    />
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import axios from "axios";
import { useStore } from "vuex";
import { OPTIMISTIC_LOCK_ALERT_MESSAGE } from "@/vue/entities/face-sheet";

import BaseInfoSection from "./user-info-tab/BaseInfoSection.vue";
import NoteSection from "./user-info-tab/NoteSection.vue";
import ChildInfoSection from "./user-info-tab/ChildInfoSection.vue";
import ConsultationHistorySection from "./user-info-tab/ConsultationHistorySection.vue";
import EpdsHistorySection from "./user-info-tab/EpdsHistorySection.vue";

const props = defineProps({
  daytimeAdviceSheet: { type: Object, required: true },
  excludeSheetId: { type: [Number, String], default: null },
});

const store = useStore();

// template内でリアクティブに参照するためcomputedで包む
const daytimeAdviceSheet = computed(() => props.daytimeAdviceSheet);

const cancelToken = axios.CancelToken;
let cancelSource = null;
// fetchChildContext は cancelSource（axios）の対象外で並行実行されるため、
// リクエストごとに世代IDを発行し、古い世代のレスポンスが新しい世代の結果を上書きしないようにする
let childRequestId = 0;

// ローカルstate（Vuex不使用）
const user = ref({});
const userKartes = ref([]);
const userAnytimeConsultations = ref([]);
const userDaytimeAdviceSheets = ref([]);
const epdsAnswerList = ref([]);

const isEditingUserNote = ref(false);
const userNoticeDraft = ref("");
const userLockVersion = ref(null);
const userNoteSaving = ref(false);
const isEditingOperatorNote = ref(false);
const operatorNoticeDraft = ref("");
const operatorNoteSaving = ref(false);
const children = ref([]);
const childrenLoading = ref(false);
const isEditingChildren = ref(false);
const childNoticeDrafts = ref({});
const childNoteSaving = ref(false);

// --- 派生computed ---

const displayCoupon = computed(() => {
  if (user.value.coupon && user.value.coupon !== "") {
    return user.value.coupon;
  }
  if (props.daytimeAdviceSheet.couponName && props.daytimeAdviceSheet.couponName !== "") {
    return props.daytimeAdviceSheet.couponName;
  }
  return "-";
});

const displayCouponId = computed(() => {
  if (user.value.couponId !== null && user.value.couponId !== undefined && user.value.couponId !== "") {
    return user.value.couponId;
  }
  if (props.daytimeAdviceSheet.couponId !== null
    && props.daytimeAdviceSheet.couponId !== undefined
    && props.daytimeAdviceSheet.couponId !== "") {
    return props.daytimeAdviceSheet.couponId;
  }
  return "";
});

const canEditNotes = computed(() => {
  const hasAllCapabilities = store.getters.hasAllCapabilities;
  if (typeof hasAllCapabilities !== "function") return false;
  return hasAllCapabilities(["counselor:access"])
    || hasAllCapabilities(["operator:access"])
    || hasAllCapabilities(["high_risk:access"]);
});

// 運営からの特記事項の編集は operator 権限のみに限定する（相談員・high-riskは閲覧のみ）。
const canEditOperatorNote = computed(() => {
  const hasAllCapabilities = store.getters.hasAllCapabilities;
  if (typeof hasAllCapabilities !== "function") return false;
  return hasAllCapabilities(["operator:access"]);
});

// 夜間相談はバックエンドでenumが定義されていない 数値と文字列の両方に対応するラベルを定義しておく
const DEPARTMENT_LABELS = {
  1: "小児科医",
  2: "産婦人科医",
  3: "助産師",
  pediatrician: "小児科医",
  obstetrician: "産婦人科医",
  midwife: "助産師",
};

const departmentLabel = (departmentType) => DEPARTMENT_LABELS[departmentType] || departmentType || "-";

const toTimestamp = (datetime) => {
  if (!datetime) return 0;
  if (typeof datetime === "number") return datetime;

  const normalized = typeof datetime === "string"
    ? datetime.trim().replace(" ", "T")
    : datetime;
  const parsed = Date.parse(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const consultationHistories = computed(() => {
  const daytimeSheets = Array.isArray(userDaytimeAdviceSheets.value) ? userDaytimeAdviceSheets.value : [];
  const anytimeConsultations = Array.isArray(userAnytimeConsultations.value) ? userAnytimeConsultations.value : [];
  const nightKartes = Array.isArray(userKartes.value) ? userKartes.value : [];

  const daytimeHistories = daytimeSheets.map((sheet) => ({
    key: `daytime-${sheet.id}`,
    sourceType: "daytime",
    id: sheet.id,
    datetime: sheet.consultedAt || [sheet.adviceDate, sheet.startAt].filter(Boolean).join(" "),
    // 日中助産師相談は子と紐づく手段がないため、対象の子は常にブランク表示にする
    childName: null,
    childId: null,
    consultationType: `日中助産師相談 / ${departmentLabel(sheet.departmentType)}`,
    complaint: sheet.complaint,
  }));

  const anytimeHistories = anytimeConsultations.map((consultation) => ({
    key: `anytime-${consultation.id}`,
    sourceType: "anytime",
    id: consultation.id,
    datetime: consultation.consultedAt || consultation.simpleAskedAt,
    childName: consultation.childName,
    childId: consultation.childId ?? null,
    consultationType: `いつでも相談 / ${departmentLabel(consultation.departmentType)}`,
    complaint: consultation.complaint,
  }));

  const nightHistories = nightKartes.map((karte) => ({
    key: `night-${karte.id}`,
    sourceType: "night",
    id: karte.id,
    reservationId: karte.reservationId,
    datetime: karte.consultedAt || [karte.date, karte.time].filter(Boolean).join(" "),
    childName: karte.childName,
    childId: karte.childId ?? karte.child_id ?? null,
    consultationType: `夜間相談 / ${departmentLabel(karte.departmentType)}`,
    complaint: karte.complaint,
  }));

  return [...daytimeHistories, ...anytimeHistories, ...nightHistories].sort((a, b) => {
    const left = toTimestamp(a.datetime);
    const right = toTimestamp(b.datetime);
    return right - left;
  });
});

const epdsHistories = computed(() => {
  const answers = Array.isArray(epdsAnswerList.value) ? epdsAnswerList.value : [];
  return answers.map((answer) => {
    const question10 = (answer.important_questions || []).find((q) => q.question_no === 10);
    return {
      id: answer.id,
      answeredAt: answer.answeredAt || answer.answered_at,
      period: answer.weeks_after_childbirth != null ? `産後${answer.weeks_after_childbirth}` : "-",
      totalScore: answer.total_score != null ? `${answer.total_score}点` : "-",
      positive: answer.positive === true,
      question10Score: question10 ? `${question10.score}点` : "-",
      question10Choice: question10 ? question10.choice : "",
    };
  });
});

// --- API呼び出し（axios直接） ---

const fetchUserContext = async () => {
  const userId = props.daytimeAdviceSheet.userId;
  const sheetId = props.excludeSheetId ?? props.daytimeAdviceSheet.id;

  if (!userId) {
    clear();
    return;
  }

  if (cancelSource) {
    cancelSource.cancel();
  }
  cancelSource = cancelToken.source();
  const token = cancelSource.token;

  await Promise.allSettled([
    axios.get("/api/v1/users", { params: { user_id: userId } })
      .then((r) => {
        user.value = r.data || {};
        userLockVersion.value = r.data?.lockVersion ?? null;
      }),

    axios.get(`/api/v1/users/${userId}/kartes`, {
      cancelToken: token,
      validateStatus: (s) => s < 500,
    }).then((r) => { userKartes.value = r.status === 404 ? [] : (r.data || []); }),

    axios.get(`/api/v1/users/${userId}/anytime_consultations`, { cancelToken: token })
      .then((r) => { userAnytimeConsultations.value = r.data || []; }),

    axios.get("/api/v1/daytime_advice_sheets/users_advice_sheets", {
      params: { user_id: userId, exclude_sheet_id: sheetId },
      cancelToken: token,
    }).then((r) => { userDaytimeAdviceSheets.value = r.data || []; }),

    axios.get("/api/v1/epds_answers", { params: { user_id: userId }, cancelToken: token })
      .then((r) => { epdsAnswerList.value = r.data || []; }),
  ]);
};

const fetchChildContext = async () => {
  const requestId = ++childRequestId;
  const userId = props.daytimeAdviceSheet.userId;
  if (!userId) {
    children.value = [];
    return;
  }
  childrenLoading.value = true;
  try {
    const r = await axios.get("/api/v1/children", {
      params: { user_id: userId },
      validateStatus: (s) => s < 500,
    });
    // 取得中に別のシートへ遷移し、より新しいリクエストが発行されていた場合は
    // このレスポンスを反映しない（stale response破棄）
    if (requestId !== childRequestId) return;
    children.value = Array.isArray(r.data) ? r.data : [];
  } finally {
    if (requestId === childRequestId) {
      childrenLoading.value = false;
    }
  }
};

// --- 公開メソッド ---

// 子の情報は他のユーザー関連情報（相談履歴等）と依存関係がないため、
// fetchUserContext の完了を待たずに並行して取得し、表示までの待ち時間を短縮する。
const start = async () => {
  await Promise.allSettled([fetchUserContext(), fetchChildContext()]);
};

const clear = () => {
  if (cancelSource) {
    cancelSource.cancel();
    cancelSource = null;
  }
  // 進行中の子情報取得リクエストがあれば、その結果を無効化する
  childRequestId += 1;
  user.value = {};
  userLockVersion.value = null;
  userNoteSaving.value = false;
  children.value = [];
  childrenLoading.value = false;
  isEditingChildren.value = false;
  childNoticeDrafts.value = {};
  childNoteSaving.value = false;
  operatorNoteSaving.value = false;
  userKartes.value = [];
  userAnytimeConsultations.value = [];
  userDaytimeAdviceSheets.value = [];
  epdsAnswerList.value = [];
};

// --- 編集操作 ---

const toggleUserNoteEdit = async () => {
  if (!canEditNotes.value) return;

  if (!isEditingUserNote.value) {
    userNoticeDraft.value = user.value.notice || "";
    isEditingUserNote.value = true;
    return;
  }

  userNoteSaving.value = true;
  try {
    await axios.put(`/api/v1/users/${user.value.id}`, {
      user: { notice: userNoticeDraft.value, lock_version: userLockVersion.value },
    }, { headers: { "Content-Type": "application/json" } });
  } catch (e) {
    if (e.response?.status === 409) {
      alert(OPTIMISTIC_LOCK_ALERT_MESSAGE);
      isEditingUserNote.value = false;
      const r = await axios.get("/api/v1/users", { params: { user_id: props.daytimeAdviceSheet.userId } });
      user.value = r.data || {};
      userLockVersion.value = r.data?.lockVersion ?? null;
      return;
    }
    throw e;
  } finally {
    userNoteSaving.value = false;
  }

  isEditingUserNote.value = false;
  const r = await axios.get("/api/v1/users", { params: { user_id: props.daytimeAdviceSheet.userId } });
  user.value = r.data || {};
  userLockVersion.value = r.data?.lockVersion ?? null;
};

const toggleOperatorNoteEdit = async () => {
  if (!canEditOperatorNote.value) return;

  if (!isEditingOperatorNote.value) {
    operatorNoticeDraft.value = user.value.operatorNotice || "";
    isEditingOperatorNote.value = true;
    return;
  }

  operatorNoteSaving.value = true;
  try {
    await axios.put(`/api/v1/users/${user.value.id}`, {
      user: { operator_notice: operatorNoticeDraft.value, lock_version: userLockVersion.value },
    }, { headers: { "Content-Type": "application/json" } });
  } catch (e) {
    if (e.response?.status === 409) {
      alert(OPTIMISTIC_LOCK_ALERT_MESSAGE);
      isEditingOperatorNote.value = false;
      const r = await axios.get("/api/v1/users", { params: { user_id: props.daytimeAdviceSheet.userId } });
      user.value = r.data || {};
      userLockVersion.value = r.data?.lockVersion ?? null;
      return;
    }
    throw e;
  } finally {
    operatorNoteSaving.value = false;
  }

  isEditingOperatorNote.value = false;
  const r = await axios.get("/api/v1/users", { params: { user_id: props.daytimeAdviceSheet.userId } });
  user.value = r.data || {};
  userLockVersion.value = r.data?.lockVersion ?? null;
};

const startChildNoteEdit = () => {
  if (!canEditNotes.value) return;
  const drafts = {};
  children.value.forEach((c) => {
    drafts[c.id] = c.notice || "";
  });
  childNoticeDrafts.value = drafts;
  isEditingChildren.value = true;
};

const saveChildNoteEdit = async () => {
  if (!isEditingChildren.value) return;

  // 変更のあった子だけPUT
  const changed = children.value.filter((c) => {
    const draft = childNoticeDrafts.value[c.id] ?? c.notice ?? "";
    return draft !== (c.notice || "");
  });

  if (changed.length === 0) {
    isEditingChildren.value = false;
    childNoticeDrafts.value = {};
    return;
  }

  childNoteSaving.value = true;
  try {
    for (const child of changed) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await axios.put(`/api/v1/children/${child.id}`, {
          child: { notice: childNoticeDrafts.value[child.id], lock_version: child.lockVersion },
        }, { headers: { "Content-Type": "application/json" } });
      } catch (e) {
        if (e.response?.status === 409) {
          alert(OPTIMISTIC_LOCK_ALERT_MESSAGE);
          childNoteSaving.value = false;
          isEditingChildren.value = false;
          childNoticeDrafts.value = {};
          await fetchChildContext();
          return;
        }
        throw e;
      }
    }
  } finally {
    childNoteSaving.value = false;
  }

  isEditingChildren.value = false;
  childNoticeDrafts.value = {};
  await fetchChildContext();
};

// --- キャンセル処理 ---

const cancelUserNoteEdit = () => {
  isEditingUserNote.value = false;
  userNoticeDraft.value = "";
};

const cancelChildNoteEdit = () => {
  isEditingChildren.value = false;
  childNoticeDrafts.value = {};
};

const cancelOperatorNoteEdit = () => {
  isEditingOperatorNote.value = false;
  operatorNoticeDraft.value = "";
};

// --- ウィンドウ開く ---

const openConsultation = (history) => {
  if (history.sourceType === "daytime") {
    window.open(`/daytime_advices_sheets/${history.id}`, "", "noopener,noreferrer,width=480,height=640");
    return;
  }
  if (history.sourceType === "anytime") {
    window.open(`/anytime_consultations/view/${history.id}`, "", "noopener,noreferrer,width=480,height=640");
    return;
  }
  if (history.reservationId) {
    window.open(`/reservations/${history.reservationId}`, "", "noopener,noreferrer,width=480,height=640");
  }
};

const openEpds = (id) => {
  window.open(`/epds/${id}`, "", "noopener,noreferrer,width=480,height=640");
};

defineExpose({
  start,
  clear,
});
</script>

<style lang="scss" scoped>
.user-info-tab {
  background-color: #f7f7f7;
  min-height: calc(100vh - 150px);
}
</style>
